import store from '@/store/store';
import { useWebsocket } from '@/utils/websocket.util';
import { checkProjectIdAndRedirect } from '@/store/plugins/bad-response-handler.plugin';
import { WEBSOCKET_EVENTS } from '@/store/modules/websocket/websocket.types';
import { accountApi } from '@/api/account.api';

export class AccountLoader {
  public isLoadingAccount = true;
  private hasAttemptedReload = sessionStorage.getItem('hasAttemptedReload') === 'true';
  public websocketClient: any;
  private vue: any;

  constructor(vue: any) {
    this.vue = vue;
    this.websocketClient = useWebsocket(vue);
  }

  get accountDetail() {
    return store.state.account.getAccount.data;
  }

  get accountDetailError() {
    return store.state.account.getAccount.error;
  }

  get accountDetailErrorEventState() {
    return store.state.websocket.accountDetailErrorEventState;
  }

  connectWebSocket(): void {
    if (!this.websocketClient.isConnected()) {
      this.websocketClient.connectToWs();
    }
    if (this.accountDetail?.id && +this.accountDetail.id) {
      this.websocketClient.joinRoom({ 
        projectId: +this.vue.$route.params.projectId, 
        event: WEBSOCKET_EVENTS.ACCOUNT_DETAILS_ERROR_EVENT 
      });
    }
  }

  async reloadAccount(): Promise<void> {
    try {
      this.isLoadingAccount = true;
      await store.dispatch('account/getAccount');
      
      if (!this.accountDetail) {
        throw new Error('Account details not loaded after reload');
      }
      
      this.connectWebSocket();
      this.isLoadingAccount = false;
    } catch (error) {
      console.error('Failed to reload account details:', error);
      this.isLoadingAccount = false;
      this.onFailedAccountRetrieve();
    }
  }

  async onFailedAccountRetrieve(): Promise<void> {
    // Show toast message
    this.vue.$buefy.toast.open({
      message: 'Failed to get account details. Attempting to refresh...',
      type: 'is-danger',
      position: 'is-top'
    });

    if (!this.hasAttemptedReload) {
      // First attempt: try reloading the window
      sessionStorage.setItem('hasAttemptedReload', 'true');
      window.location.reload();
      return; // Exit function to wait for reload
    }

    // If we're here, reload already attempted and failed, so redirect
    this.vue.$buefy.toast.open({
      message: 'Refresh failed. Redirecting to main page...',
      type: 'is-danger',
      position: 'is-top'
    });

    // Redirect to SUMARI main page
    setTimeout(() => {
      sessionStorage.removeItem('hasAttemptedReload');
      window.location.replace(`${process.env.VUE_APP_PROXY_CLIENT_URL}`);
    }, 3000);
  }

  async initializeAccount(route: any): Promise<void> {
    try {
      // Check for routes that should redirect to proxy
      const routePath = route.path;
      
      // Redirect if URL is exactly /projects/ (with trailing slash) or base URL
      if (routePath === '/projects/' || routePath === '') {
        checkProjectIdAndRedirect(undefined); // This will trigger redirect
        return;
      }
      
      // Only check for projectId on routes that actually require it (routes starting with /projects/:projectId)
      const isProjectRoute = routePath.startsWith('/projects/') && routePath !== '/projects';
      
      if (isProjectRoute) {
        const projectId = route.params.projectId as string;
        if (checkProjectIdAndRedirect(projectId)) {
          return; // Exit early if redirect happened
        }
      }

      // Clear any previous reload attempt flag on fresh mount
      sessionStorage.removeItem('hasAttemptedReload');

      // Set loading state
      this.isLoadingAccount = true;

      // Check for pre-passed account data from SUMARI redirect
      const preloadData = this.tryLoadPrepassedAccount();
      if (preloadData) {
        // Commit preloaded data immediately so UI renders instantly
        store.commit('GET_ACCOUNT_MUTATION', { data: preloadData });

        // Push preloaded data to BCM backend session cache before allowing API calls
        try {
          await accountApi.pushAccountToBcm(preloadData);
        } catch (e) {
          console.warn('Failed to push preloaded account to BCM session cache:', e);
        }

        this.connectWebSocket();
        this.isLoadingAccount = false;

        // Still fetch full account in background to validate/refresh
        store.dispatch('account/getAccount').catch(() => {
          // If background refresh fails, preloaded data is still usable
        });
        return;
      }

      // No preload — block until account is loaded (normal flow)
      await store.dispatch('account/getAccount');

      // Only proceed if we have account details
      if (!this.accountDetail) {
        throw new Error('Account details not loaded');
      }

      // Connect WebSocket only after successful account load
      this.connectWebSocket();

      // Set loading to false only after everything is successful
      this.isLoadingAccount = false;

    } catch (error) {
      console.error('Failed to load account details:', error);
      this.isLoadingAccount = false;
      // Trigger the error handling which will attempt reload or redirect
      this.onFailedAccountRetrieve();
    }
  }

  private tryLoadPrepassedAccount(): any | null {
    try {
      const raw = sessionStorage.getItem('sumari_account_preload');
      if (!raw) return null;
      sessionStorage.removeItem('sumari_account_preload');
      const parsed = JSON.parse(raw);
      if (parsed && parsed.id && parsed.email) {
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  }

  onAccountDetailChange(newVal: any, oldVal: any): void {
    // If account detail was previously loaded but now is null/undefined, reload
    if (oldVal && !newVal && !this.isLoadingAccount) {
      console.warn('Account detail lost, reloading...');
      this.reloadAccount();
    }
  }
}

export function useAccountLoader(vue: any): AccountLoader {
  return new AccountLoader(vue);
} 